import type { ReactNode } from "react";
import {
  UserOutlined,
  BankOutlined,
  ContainerOutlined,
  ApiOutlined,
} from "@ant-design/icons";

export type MatchReason =
  | "company_invoice_email"
  | "company_phone"
  | "user_email";

export interface CompanyMatchRow {
  id: number;
  name: string;
  status?: string | null;
  phone_number?: string | null;
  invoice_email?: string | null;
  city?: string | null;
  country?: string | null;
  matchReasons: MatchReason[];
}

export interface CompanyUserRow {
  id: string;
  email: string;
  created_at?: string;
}

export interface LookupResult {
  company: CompanyMatchRow;
  users: CompanyUserRow[];
}

export type BookingInsight = Record<string, unknown> & {
  id: number;
  reference: string | null;
  status?: string | null;
  eta?: string | null;
  commercial_invoice_file?: string | null;
  transport_type?: {
    name?: string | null;
    abbreviation?: string | null;
  } | null;
  /** @deprecated legacy alias — prefer transport_type */
  transport_type_info?: {
    name?: string | null;
    abbreviation?: string | null;
  } | null;
  container?: BookingInsightContainer | null;
  products?: unknown[];
  last_mile_shipments?: unknown[];
  timeline?: {
    comments?: unknown[];
    notifications?: unknown[];
  };
  lean_customs?: unknown;
  master_container?: unknown;
  company_id?: number;
  container_id?: number | null;
  leancustoms_order_id?: string | null;
  leancustoms_declaration_id?: string | null;
};

type BookingInsightContainer = {
  reference?: string | null;
  etd?: string | null;
  delayed_etd?: string | null;
  eta?: string | null;
  eta_customer?: string | null;
  status?: string | null;
  track_and_trace?: string | null;
  bl_number?: string | null;
  origin_port?: { name?: string | null; country?: string | null } | null;
  destination_port?: { name?: string | null; country?: string | null } | null;
};

export const MATCH_LABELS: Record<MatchReason, string> = {
  company_invoice_email: "Company invoice email",
  company_phone: "Company phone",
  user_email: "Linked user email",
};

/** Insights modes for step 3. */
export type BotInsightsMode = "lookup" | "recent" | "all" | "status";

/** Active (non-terminal) statuses for “recent open bookings”. */
export const OPEN_BOOKING_STATUSES = [
  "submitted",
  "approved",
  "waiting_for_goods",
  "goods_received_origin",
  "in_transit",
  "customs_pending_clearance",
  "custom_clearance",
  "customs_cleared",
  "fyco",
  "out_for_delivery",
  "delivery_planned",
] as const;

/** Compact list row for browse modes (not full bot JSON). */
export const BOOKING_LIST_SELECT = `
id, reference, status, created_at, eta,
transport_type:booking_transport_types(name, abbreviation),
container:containers(reference, status, etd, delayed_etd, eta, eta_customer)
`;

export interface BookingListRow {
  id: number;
  reference: string | null;
  status: string | null;
  created_at: string | null;
  eta: string | null;
  transport_type?: { name?: string | null; abbreviation?: string | null } | null;
  container?: {
    reference?: string | null;
    status?: string | null;
    etd?: string | null;
    delayed_etd?: string | null;
    eta?: string | null;
    eta_customer?: string | null;
  } | null;
}

export function formatBookingEffLabel(row: {
  id: number;
  reference?: string | null;
  transport_type?: { abbreviation?: string | null } | null;
}): string {
  const abbr = (row.transport_type?.abbreviation ?? "").trim().toUpperCase();
  return row.reference || `EFF${row.id}${abbr}`;
}

/**
 * Fields aligned with customer booking view (`/bookings/:id/view` / BookingTracker).
 * Omits raw storage file blobs; document presence is derived in the payload builder.
 */
export const BOOKING_INSIGHT_SELECT = `
id, reference, status, created_at, eta, service_level, company_id, storage_option,
notes, rejection_reason,
origin_pickup_date, confirmed_pick_up_date, origin_received_date,
export_clearance, pick_up_fee, commodity_inspection, post_export_license,
unloading_status, unloaded_at, total_actual_cartons, total_actual_pallets, unloading_notes,
leancustoms_order_id, leancustoms_declaration_id,
commercial_invoice_file,
transport_type:booking_transport_types(name, abbreviation),
booking_type:bookings_type(name),
booking_incoterm:bookings_incoterm(name),
origin_port:booking_origin_port(name, country),
destination_port:booking_destination_port(name, country),
supplier:booking_supplier(name, email, phone, country, city, address),
products:booking_product(
  id, name, hs_code, gross_weight, cbm, value, ean, pieces, cartons
),
delivery_details:booking_delivery(
  id, country, city, postal_code, street_name, house_number, instructions
),
container:containers(
  id, reference, bl_number, track_and_trace, status,
  etd, delayed_etd, eta, eta_customer,
  origin_port:booking_origin_port(name, country),
  destination_port:booking_destination_port(name, country),
  master_container_info:master_containers_containers(
    master_container_id,
    master_containers(id, reference)
  )
),
invoices:booking_invoice(id, created_at),
other_documents:booking_other_documents(id, created_at)
`;

/** Only strip raw file/path fields — view page shows the rest to the customer. */
const BOT_OMIT_KEYS = new Set([
  "commercial_invoice_file",
  "shipping_label",
  "booking_invoice_file",
  "booking_other_documents_file",
]);

function stripForBot(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripForBot);
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(
      value as Record<string, unknown>,
    )) {
      if (BOT_OMIT_KEYS.has(key)) continue;
      out[key] = stripForBot(nested);
    }
    return out;
  }
  return value;
}

function getTransportAbbr(booking: BookingInsight): string {
  const transport = booking.transport_type ?? booking.transport_type_info;
  if (!transport || Array.isArray(transport)) return "";
  return (transport.abbreviation ?? "").trim().toUpperCase();
}

function asObjectArray(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Record<string, unknown> =>
      !!item && typeof item === "object",
  );
}

/**
 * Shape a booking row into the JSON object the bot would receive —
 * mirrors the customer booking view page sections.
 */
export function buildBotBookingPayload(
  booking: BookingInsight,
  companyId: number,
): Record<string, unknown> {
  const abbr = getTransportAbbr(booking);
  const effReference = `EFF${booking.id}${abbr}`;
  const cleaned = stripForBot(booking) as Record<string, unknown>;

  const container = (cleaned.container ?? null) as BookingInsightContainer | null;
  const products = asObjectArray(cleaned.products);
  const deliveryDetails = asObjectArray(cleaned.delivery_details);
  const invoices = asObjectArray(cleaned.invoices);
  const otherDocuments = asObjectArray(cleaned.other_documents);
  const commercialInvoiceRaw =
    typeof booking.commercial_invoice_file === "string"
      ? booking.commercial_invoice_file
      : "";
  const commercialInvoiceCount = commercialInvoiceRaw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean).length;

  const customerEta = booking.eta || container?.eta_customer || null;
  const containerEtd = container?.delayed_etd || container?.etd || null;

  const productSummary = {
    count: products.length,
    total_pieces: products.reduce(
      (sum, p) => sum + (Number(p.pieces) || 0),
      0,
    ),
    total_cartons: products.reduce(
      (sum, p) => sum + (Number(p.cartons) || 0),
      0,
    ),
    total_cbm: products.reduce((sum, p) => sum + (Number(p.cbm) || 0), 0),
    total_gross_weight: products.reduce(
      (sum, p) => sum + (Number(p.gross_weight) || 0),
      0,
    ),
  };

  const deliveryType =
    deliveryDetails.length > 0 ? "address" : "storage";

  return {
    // Identity (same header as view page)
    company_id: companyId,
    eff_reference: effReference,
    display_reference: booking.reference || effReference,
    id: cleaned.id,
    reference: cleaned.reference,
    status: cleaned.status,
    created_at: cleaned.created_at,
    notes: cleaned.notes ?? null,
    rejection_reason: cleaned.rejection_reason ?? null,

    // Transport details card
    transport_type: cleaned.transport_type ?? null,
    booking_type: cleaned.booking_type ?? null,
    service_level: cleaned.service_level ?? null,
    booking_incoterm: cleaned.booking_incoterm ?? null,
    origin_pickup_date: cleaned.origin_pickup_date ?? null,
    confirmed_pick_up_date: cleaned.confirmed_pick_up_date ?? null,
    origin_received_date: cleaned.origin_received_date ?? null,
    customer_eta: customerEta,
    booking_eta_override: cleaned.eta ?? null,
    origin_port: cleaned.origin_port ?? null,
    destination_port: cleaned.destination_port ?? null,

    // Extra services card
    extra_services: {
      export_clearance: cleaned.export_clearance ?? null,
      pick_up_fee: cleaned.pick_up_fee ?? null,
      commodity_inspection: cleaned.commodity_inspection ?? null,
      post_export_license: cleaned.post_export_license ?? null,
    },

    // Container + tracking (view page container card)
    container: container
      ? {
          id: (container as { id?: number }).id ?? null,
          reference: container.reference ?? null,
          bl_number: container.bl_number ?? null,
          status: container.status ?? null,
          etd: container.etd ?? null,
          delayed_etd: container.delayed_etd ?? null,
          effective_etd: containerEtd,
          eta: container.eta ?? null,
          eta_customer: container.eta_customer ?? null,
          track_and_trace: container.track_and_trace ?? null,
          origin_port: container.origin_port ?? null,
          destination_port: container.destination_port ?? null,
        }
      : null,

    // Delivery details card
    delivery_type: deliveryType,
    storage_option: cleaned.storage_option ?? null,
    delivery_details: deliveryDetails,

    // Supplier card (same fields as view page)
    supplier: cleaned.supplier ?? null,

    // Products card
    products,
    product_summary: productSummary,

    // Unloading / warehouse facts
    unloading: {
      unloading_status: cleaned.unloading_status ?? null,
      unloaded_at: cleaned.unloaded_at ?? null,
      total_actual_cartons: cleaned.total_actual_cartons ?? null,
      total_actual_pallets: cleaned.total_actual_pallets ?? null,
      unloading_notes: cleaned.unloading_notes ?? null,
    },

    // Documents (presence only — no storage paths)
    documents: {
      has_commercial_invoice: commercialInvoiceCount > 0,
      commercial_invoice_count: commercialInvoiceCount,
      invoice_count: invoices.length,
      other_document_count: otherDocuments.length,
    },

    // LeanCustoms (loaded when linked / company has LC org)
    customs: {
      leancustoms_order_id: cleaned.leancustoms_order_id ?? null,
      leancustoms_declaration_id: cleaned.leancustoms_declaration_id ?? null,
      lean_customs: booking.lean_customs ?? null,
    },

    // Master B/L — sibling bookings are same-company only (never other tenants)
    master_container: booking.master_container ?? null,

    // Last-mile (Sendcloud) — same section as view page
    last_mile_shipments: Array.isArray(booking.last_mile_shipments)
      ? booking.last_mile_shipments
      : [],

    // Timeline card (comments + status change notifications)
    timeline: booking.timeline ?? { comments: [], notifications: [] },
  };
}

/** Pretty-printed JSON (indented) for preview / LLM context. */
export function formatBotBookingJson(payload: Record<string, unknown>): string {
  return JSON.stringify(payload, null, 2);
}

/** Planned bot pipeline — UI only; no public endpoints yet. */
export const PIPELINE_STEPS: Array<{
  key: string;
  title: string;
  subtitle: string;
  endpointHint: string;
  icon: ReactNode;
  status: "active" | "planned";
}> = [
  {
    key: "resolve",
    title: "1. Resolve identity",
    subtitle: "Email / phone → companies",
    endpointHint: "Later: POST …/bot/resolve",
    icon: <UserOutlined />,
    status: "active",
  },
  {
    key: "company",
    title: "2. Pick company",
    subtitle: "Scope to one tenant",
    endpointHint: "Uses resolve result (no separate call)",
    icon: <BankOutlined />,
    status: "active",
  },
  {
    key: "insights",
    title: "3. Booking insights",
    subtitle: "Lookup by id / reference",
    endpointHint: "Later: GET …/bot/bookings",
    icon: <ContainerOutlined />,
    status: "active",
  },
  {
    key: "reply",
    title: "4. Bot reply",
    subtitle: "Ask a question (OpenAI)",
    endpointHint: "Admin preview: bot-reply",
    icon: <ApiOutlined />,
    status: "active",
  },
];

export function normalizePhone(value: string): string {
  return value.replace(/\D/g, "");
}

export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function phonesLikelyMatch(
  stored: string | null | undefined,
  query: string,
): boolean {
  if (!stored || !query) return false;
  const a = normalizePhone(stored);
  const b = normalizePhone(query);
  if (!a || !b) return false;
  return a.includes(b) || b.includes(a);
}

/** Extract numeric booking id from chat-like variants: 7756, EFF7756, EFF7756SEA. */
export function extractBookingIdFromQuery(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const effMatch = /EFF\s*(\d+)/i.exec(trimmed);
  if (effMatch) {
    const id = Number(effMatch[1]);
    return Number.isFinite(id) ? id : null;
  }

  if (/^\d+$/.test(trimmed)) {
    const id = Number(trimmed);
    return Number.isFinite(id) ? id : null;
  }

  return null;
}

/**
 * Build PostgREST OR conditions for booking lookup.
 * Matches id and/or reference for variants like EFF7756SEA / EFF7756 / 7756.
 */
export function buildBookingLookupOrFilter(raw: string): string {
  const trimmed = raw.trim();
  const conditions: string[] = [];

  // Always try reference (exact-ish + contains for pasted variants)
  conditions.push(`reference.ilike.${trimmed}`);
  conditions.push(`reference.ilike.%${trimmed}%`);

  const id = extractBookingIdFromQuery(trimmed);
  if (id != null) {
    conditions.push(`id.eq.${id}`);
  }

  return conditions.join(",");
}

/** Prefer exact id, then exact reference, then first partial. */
export function pickBestBookingMatch<
  T extends { id: number; reference: string | null },
>(rows: T[], raw: string): T | null {
  if (rows.length === 0) return null;
  if (rows.length === 1) return rows[0];

  const trimmed = raw.trim().toLowerCase();
  const id = extractBookingIdFromQuery(raw);

  if (id != null) {
    const byId = rows.find((r) => r.id === id);
    if (byId) return byId;
  }

  const exactRef = rows.find(
    (r) => (r.reference ?? "").trim().toLowerCase() === trimmed,
  );
  if (exactRef) return exactRef;

  return rows[0];
}

/**
 * Pull a booking token from free-text chat, e.g.
 * "Waar is EFF7668SEA?" → "EFF7668SEA"
 */
export function extractBookingTokenFromMessage(message: string): string | null {
  const text = message.trim();
  if (!text) return null;

  const eff = text.match(/EFF\s*\d+[A-Za-z]*/i);
  if (eff?.[0]) return eff[0].replace(/\s+/g, "").toUpperCase();

  const bare = text.match(/\b(\d{3,6})\b/);
  if (bare?.[1]) return bare[1];

  return null;
}
